import React from 'react';

const SmartInput = ({
  type = 'text',
  name,
  value,
  onChange,
  placeholder,
  availableVariables = [],
  rows = 4,
  style = {},
  disabled = false,
  icon = null,
  className = "",
  ...props
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const dropdownRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!isFocused) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [isFocused]);

  const truncateData = (str) => {
    if (typeof str !== 'string') return '';
    if (str.length > 78) {
      return str.substring(0, 78) + '...';
    }
    return str;
  };

  const parseVariable = (val) => {
    if (typeof val === 'string' && val.startsWith('{{var:') && val.endsWith('}}')) {
      const parts = val.substring(6, val.length - 2).split('|');
      if (parts.length >= 3) {
        return {
          element: parts[0],
          campo: parts[1],
          dato: parts.slice(2).join('|')
        };
      }
    }
    return null;
  };

  const parsed = parseVariable(value);

  const filtered = availableVariables.filter(item => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (item.campo || '').toLowerCase().includes(q) ||
      (item.dato || '').toLowerCase().includes(q) ||
      (item.element || '').toLowerCase().includes(q)
    );
  });

  const grouped = {};
  filtered.forEach((item, globalIdx) => {
    const itemWithIdx = { ...item, globalIndex: globalIdx };
    if (!grouped[item.element]) grouped[item.element] = [];
    grouped[item.element].push(itemWithIdx);
  });

  const handleSelect = (item) => {
    const varString = `{{var:${item.element}|${item.campo}|${item.dato}}}`;
    onChange(varString);
    setIsFocused(false);
    setSearchQuery('');
    setActiveIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (filtered.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < filtered.length) {
        e.preventDefault();
        handleSelect(filtered[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsFocused(false);
    }
  };

  if (parsed) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        background: 'rgba(139, 92, 246, 0.08)',
        border: '1px solid rgba(139, 92, 246, 0.4)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-2) var(--space-3)',
        width: '100%',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
        minHeight: type === 'textarea' ? '80px' : '40px',
        ...style
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            background: 'rgba(139, 92, 246, 0.15)',
            border: '1px solid #8b5cf6',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--space-1) var(--space-3)',
            fontSize: '11px',
            color: '#fff',
            fontWeight: '600'
          }}>
            <span style={{
              background: '#000',
              color: '#fff',
              borderRadius: 'var(--radius-xl)',
              padding: '2px var(--space-2)',
              fontSize: '9px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              display: 'inline-block'
            }}>
              {parsed.campo}
            </span>
            <span>{truncateData(parsed.dato)}</span>
          </div>
          <span style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 'var(--radius-sm)',
            padding: '2px var(--space-2)',
            fontSize: '9px',
            color: 'rgba(255,255,255,0.4)',
            textTransform: 'uppercase'
          }}>
            {parsed.element}
          </span>
        </div>
        <button
          type="button"
          aria-label="Quitar variable"
          onClick={() => onChange('')}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.4)',
            cursor: 'pointer',
            fontSize: '14px',
            padding: 'var(--space-1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s',
            outline: 'none'
          }}
          onMouseOver={(e) => e.target.style.color = '#ef4444'}
          onMouseOut={(e) => e.target.style.color = 'rgba(255,255,255,0.4)'}
        >
          ✕
        </button>
      </div>
    );
  }

  const isMachote = className.includes('op-sub-panel-machote');

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div className={`op-input-wrapper ${icon && !isMachote ? 'has-icon' : ''}`} style={{ marginBottom: 0 }}>
        {icon && !isMachote && (
          <div className="op-input-icon" style={{ top: type === 'textarea' ? '12px' : '50%', transform: type === 'textarea' ? 'none' : 'translateY(-50%)' }}>
            {icon}
          </div>
        )}
        {type === 'textarea' ? (
          <textarea
            ref={inputRef}
            className={className}
            disabled={disabled}
            placeholder={placeholder}
            rows={rows}
            name={name}
            aria-label={name}
            value={value || ''}
            onChange={(e) => {
              onChange(e.target.value);
              setSearchQuery(e.target.value);
              setActiveIndex(-1);
            }}
            onFocus={() => {
              setIsFocused(true);
              setSearchQuery(value || '');
              setActiveIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            style={style}
            {...props}
          />
        ) : (
          <input
            ref={inputRef}
            type={type}
            className={className}
            disabled={disabled}
            placeholder={placeholder}
            name={name}
            aria-label={name}
            value={value || ''}
            onChange={(e) => {
              onChange(e.target.value);
              setSearchQuery(e.target.value);
              setActiveIndex(-1);
            }}
            onFocus={() => {
              setIsFocused(true);
              setSearchQuery(value || '');
              setActiveIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            style={style}
            {...props}
          />
        )}
      </div>

      {isFocused && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 'var(--space-2)',
            background: '#121212',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.6)',
            zIndex: 10000,
            overflow: 'hidden'
          }}
        >
          <div style={{
            background: '#000',
            padding: 'var(--space-2) var(--space-3)',
            fontSize: '9px',
            fontWeight: '800',
            color: 'rgba(255,255,255,0.5)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>Variables disponibles</span>
            <button
              type="button"
              aria-label="Cerrar lista de variables"
              onClick={(e) => {
                e.stopPropagation();
                setIsFocused(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                fontSize: '11px',
                padding: '2px var(--space-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                outline: 'none',
                transition: 'color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.color = '#ef4444'}
              onMouseOut={(e) => e.target.style.color = 'rgba(255,255,255,0.4)'}
            >
              ✕
            </button>
          </div>

          <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
            {Object.entries(grouped).map(([element, items]) => (
              <div key={element}>
                <div style={{
                  background: 'rgba(255,255,255,0.02)',
                  padding: 'var(--space-1) var(--space-3)',
                  fontSize: '9px',
                  fontWeight: '700',
                  color: '#f97316',
                  textTransform: 'uppercase',
                  borderBottom: '1px solid rgba(255,255,255,0.03)',
                  letterSpacing: '0.5px'
                }}>
                  {element}
                </div>
                {items.map((item) => {
                  const isSelected = item.globalIndex === activeIndex;
                  return (
                    <div
                      key={item.campo}
                      onClick={() => handleSelect(item)}
                      style={{
                        padding: 'var(--space-2) var(--space-4)',
                        cursor: 'pointer',
                        background: isSelected ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                        color: isSelected ? '#fff' : 'rgba(255,255,255,0.8)',
                        fontSize: '11px',
                        borderBottom: '1px solid rgba(255,255,255,0.02)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'background 0.1s'
                      }}
                      onMouseEnter={() => setActiveIndex(item.globalIndex)}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontWeight: '600' }}>
                          {item.campo}
                        </span>
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>
                          {truncateData(item.dato)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartInput;
